/**
 * Blend Discovery Platform — typography design tokens.
 *
 * Single-font system: **Montserrat** for everything (Display, H1–H5, Body,
 * Button, Label, Caption, Overline). The earlier two-font system (Source
 * Serif 4 + DM Sans, commit 44bb968) was reverted on 2026-05-18 in favor of
 * a unified Montserrat stack matching the Figma design system.
 *
 * Consume via `typographyScale[variant]` in components or reference the
 * matching CSS custom properties defined in `styles/typography.css`.
 */

// ─── Font families ────────────────────────────────────────────────────────────

export const fontFamily = {
  sans: "'Montserrat', system-ui, sans-serif",
} as const;

// ─── Font weights ─────────────────────────────────────────────────────────────

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
} as const;

// ─── Font sizes (base: 1rem = 16px) ──────────────────────────────────────────

export const fontSize = {
  display: '3.5rem',
  h1: '2.5rem',
  h2: '2rem',
  h3: '1.5rem',
  h4: '1.25rem',
  h5: '1rem',
  bodyLarge: '1rem',
  bodyDefault: '0.875rem',
  bodySmall: '0.75rem',
  bodyLargeStrong: '1rem',
  bodyDefaultStrong: '0.875rem',
  buttonLarge: '1rem',
  buttonDefault: '0.875rem',
  buttonSmall: '0.75rem',
  labelUI: '0.75rem',
  caption: '0.6875rem',
  overline: '0.6875rem',
} as const;

// ─── Line heights ─────────────────────────────────────────────────────────────

export const lineHeight = {
  display: '4rem',
  h1: '3rem',
  h2: '2.5rem',
  h3: '2rem',
  h4: '1.75rem',
  h5: '1.5rem',
  bodyLarge: '1.5rem',
  bodyDefault: '1.375rem',
  bodySmall: '1.125rem',
  bodyLargeStrong: '1.5rem',
  bodyDefaultStrong: '1.375rem',
  buttonLarge: '1.5rem',
  buttonDefault: '1.25rem',
  buttonSmall: '1rem',
  labelUI: '1rem',
  caption: '1rem',
  overline: '1rem',
} as const;

// ─── Letter spacing ───────────────────────────────────────────────────────────

export const letterSpacing = {
  display: '-0.02em',
  h1: '-0.02em',
  h2: '-0.01em',
  h3: '-0.01em',
  h4: '0em',
  h5: '0em',
  bodyLarge: '0em',
  bodyDefault: '0em',
  bodySmall: '0em',
  bodyLargeStrong: '0em',
  bodyDefaultStrong: '0em',
  buttonLarge: '0em',
  buttonDefault: '0em',
  buttonSmall: '0em',
  labelUI: '0em',
  caption: '0em',
  overline: '0.08em',
} as const;

// ─── Paragraph spacing ────────────────────────────────────────────────────────

export const paragraphSpacing = {
  body: '0.75rem',
} as const;

// ─── Type scale ───────────────────────────────────────────────────────────────

export type TypographyVariant =
  | 'display'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'bodyLarge'
  | 'bodyDefault'
  | 'bodySmall'
  | 'bodyLargeStrong'
  | 'bodyDefaultStrong'
  | 'buttonLarge'
  | 'buttonDefault'
  | 'buttonSmall'
  | 'labelUI'
  | 'caption'
  | 'overline';

export interface TypographyToken {
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  lineHeight: string;
  letterSpacing: string;
}

export const typographyScale: Record<TypographyVariant, TypographyToken> = {
  display: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.light,
    fontSize: fontSize.display,
    lineHeight: lineHeight.display,
    letterSpacing: letterSpacing.display,
  },
  h1: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.h1,
    lineHeight: lineHeight.h1,
    letterSpacing: letterSpacing.h1,
  },
  h2: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.h2,
    lineHeight: lineHeight.h2,
    letterSpacing: letterSpacing.h2,
  },
  h3: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.h3,
    lineHeight: lineHeight.h3,
    letterSpacing: letterSpacing.h3,
  },
  h4: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.semibold,
    fontSize: fontSize.h4,
    lineHeight: lineHeight.h4,
    letterSpacing: letterSpacing.h4,
  },
  h5: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.semibold,
    fontSize: fontSize.h5,
    lineHeight: lineHeight.h5,
    letterSpacing: letterSpacing.h5,
  },
  bodyLarge: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.bodyLarge,
    lineHeight: lineHeight.bodyLarge,
    letterSpacing: letterSpacing.bodyLarge,
  },
  bodyDefault: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.bodyDefault,
    lineHeight: lineHeight.bodyDefault,
    letterSpacing: letterSpacing.bodyDefault,
  },
  bodySmall: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.bodySmall,
    lineHeight: lineHeight.bodySmall,
    letterSpacing: letterSpacing.bodySmall,
  },
  bodyLargeStrong: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.bodyLargeStrong,
    lineHeight: lineHeight.bodyLargeStrong,
    letterSpacing: letterSpacing.bodyLargeStrong,
  },
  bodyDefaultStrong: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.bodyDefaultStrong,
    lineHeight: lineHeight.bodyDefaultStrong,
    letterSpacing: letterSpacing.bodyDefaultStrong,
  },
  buttonLarge: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.buttonLarge,
    lineHeight: lineHeight.buttonLarge,
    letterSpacing: letterSpacing.buttonLarge,
  },
  buttonDefault: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.buttonDefault,
    lineHeight: lineHeight.buttonDefault,
    letterSpacing: letterSpacing.buttonDefault,
  },
  buttonSmall: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.buttonSmall,
    lineHeight: lineHeight.buttonSmall,
    letterSpacing: letterSpacing.buttonSmall,
  },
  labelUI: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.labelUI,
    lineHeight: lineHeight.labelUI,
    letterSpacing: letterSpacing.labelUI,
  },
  caption: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.caption,
    lineHeight: lineHeight.caption,
    letterSpacing: letterSpacing.caption,
  },
  overline: {
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.semibold,
    fontSize: fontSize.overline,
    lineHeight: lineHeight.overline,
    letterSpacing: letterSpacing.overline,
  },
};
